import { Controller } from "@hotwired/stimulus"

// streak-celebration
// ---------------------------------------------------------------
// Powers two micro-interactions on the streak page hero:
//
//   1. Count-up — the big streak number ticks from 0 to its real
//      value on first paint (~700ms ease-out-quint). Skipped under
//      prefers-reduced-motion.
//
//   2. Tap-to-celebrate — when today is already logged, tapping
//      the streak number fires a native haptic via the existing
//      bridge--haptic controller AND spawns a short-lived confetti
//      burst (DOM particles, GPU transforms only, ~1100ms total).
//      Locked behind a 1.2s cooldown so rapid taps don't pile.
export default class extends Controller {
  static targets = ["number", "burst", "hapticTrigger"]
  static values = {
    count:         Number,
    canCelebrate:  { type: Boolean, default: false },
    countDuration: { type: Number,  default: 720 }
  }

  connect() {
    this._burstCooldown = false
    if (this.hasNumberTarget) this.numberTarget.textContent = String(this.countValue)
  }

  // ── Tap-to-celebrate ───────────────────────────────────────
  celebrate(event) {
    if (!this.canCelebrateValue) return
    if (this._burstCooldown) return
    this._burstCooldown = true
    window.setTimeout(() => { this._burstCooldown = false }, 1200)

    this.tapHaptic()
    this.pulseNumber()
    if (!this.reducedMotion) this.spawnConfetti()
  }

  tapHaptic() {
    if (!this.hasHapticTriggerTarget) return
    const ctrl = this.application.getControllerForElementAndIdentifier(
      this.hapticTriggerTarget, "bridge--haptic"
    )
    ctrl?.tap?.({ type: "celebration" })
  }

  pulseNumber() {
    if (!this.hasNumberTarget) return
    this.numberTarget.animate(
      [
        { transform: "scale(1)" },
        { transform: "scale(1.08)", offset: 0.45 },
        { transform: "scale(1)" }
      ],
      { duration: 420, easing: "cubic-bezier(0.22,1,0.36,1)", fill: "forwards" }
    )
  }

  // Lightweight DOM confetti — 18 particles, GPU-only transforms.
  // Colors are sampled from the amber / orange / honey palette so the
  // burst reads as "streak fire" rather than generic party confetti.
  spawnConfetti() {
    if (!this.hasBurstTarget) return
    const palette = [
      "#f59e0b", "#fb923c", "#fbbf24", "#f97316", "#fde68a", "#facc15"
    ]
    const COUNT = 18
    const host  = this.burstTarget

    for (let i = 0; i < COUNT; i++) {
      const piece = document.createElement("span")
      const angle = (Math.PI * 2 * i) / COUNT + (Math.random() * 0.4 - 0.2)
      const dist  = 60 + Math.random() * 60
      const dx    = Math.cos(angle) * dist
      const dy    = Math.sin(angle) * dist - 18 // bias upward
      const size  = 5 + Math.random() * 5
      const color = palette[Math.floor(Math.random() * palette.length)]
      const rot   = (Math.random() * 540) - 270
      const isCircle = Math.random() < 0.35

      piece.style.cssText = `
        position: absolute;
        inset-inline-start: 50%;
        inset-block-start: 50%;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: ${isCircle ? "50%" : "2px"};
        pointer-events: none;
        will-change: transform, opacity;
        transform: translate(-50%, -50%) rotate(0deg);
      `
      host.appendChild(piece)

      const anim = piece.animate(
        [
          { transform: `translate(-50%, -50%) rotate(0deg) scale(0.6)`, opacity: 1 },
          { transform: `translate(calc(-50% + ${dx * 0.55}px), calc(-50% + ${dy * 0.55}px)) rotate(${rot * 0.6}deg) scale(1)`, opacity: 1, offset: 0.55 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + 30}px)) rotate(${rot}deg) scale(0.85)`, opacity: 0 }
        ],
        { duration: 950 + Math.random() * 250, easing: "cubic-bezier(0.18, 0.7, 0.3, 1)", fill: "forwards" }
      )
      anim.onfinish = () => piece.remove()
    }
  }

  get reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
